import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { publicationService } from '@/lib/api/services/publication'
import type {
  Publication,
  PublicationCategory,
  PublicationFilterParams,
  PublicationStats,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  CreatePublication,
  UpdatePublication
} from '@/lib/api/types'

// Query keys
export const publicationKeys = {
  all: ['publications'] as const,
  lists: () => [...publicationKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: PublicationFilterParams) =>
    [...publicationKeys.lists(), params, filters] as const,
  search: (query: string, params: PaginationParams) => [...publicationKeys.lists(), 'search', query, params] as const,
  details: () => [...publicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...publicationKeys.details(), id] as const,
  stats: () => [...publicationKeys.all, 'stats'] as const,
  categories: () => [...publicationKeys.all, 'categories'] as const,
  highlights: (type: string) => [...publicationKeys.all, 'highlights', type] as const,
}

// Hook to fetch publications with pagination and filters
export function usePublications(
  params: PaginationParams = { page: 1, size: 50 },
  filters: PublicationFilterParams = {},
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Publication>>, Error>({
    queryKey: publicationKeys.list(params, filters),
    queryFn: () => publicationService.getPublications(params, filters),
    enabled,
  })
}

// Hook to fetch publication stats
export function usePublicationStats() {
  return useQuery<PublicationStats, Error>({
    queryKey: publicationKeys.stats(),
    queryFn: async () => {
      const response = await publicationService.getPublicationStats()
      return response.data
    },
  })
}

// Hook to fetch a single publication by ID
export function usePublication(publicationId: string) {
  return useQuery<ApiResponse<Publication>, Error>({
    queryKey: publicationKeys.detail(publicationId),
    queryFn: () => publicationService.getPublicationById(publicationId),
    enabled: !!publicationId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to create a publication
export function useCreatePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Publication>, Error, CreatePublication>({
    mutationFn: (data: CreatePublication) =>
      publicationService.createPublication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to update a publication
export function useUpdatePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Publication>, Error, { publicationId: string; data: UpdatePublication }>({
    mutationFn: ({ publicationId, data }) =>
      publicationService.updatePublication(publicationId, data),
    onSuccess: (_, { publicationId }) => {
      queryClient.invalidateQueries({ queryKey: publicationKeys.detail(publicationId) })
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to update publication status
export function useUpdatePublicationStatus() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Publication>, Error, { publicationId: string; status: 'DRAFT' | 'PUBLISH' }>({
    mutationFn: ({ publicationId, status }) =>
      publicationService.updatePublicationStatus(publicationId, status),
    onSuccess: (response, { publicationId }) => {
      if (response.success && response.data) {
        queryClient.setQueryData(publicationKeys.detail(publicationId), response)
      }
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to delete a publication
export function useDeletePublication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, string>({
    mutationFn: (publicationId: string) =>
      publicationService.deletePublication(publicationId),
    onSuccess: () => {
      // Invalidate and refetch publications list
      queryClient.invalidateQueries({ queryKey: publicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: publicationKeys.stats() })
    },
  })
}

// Hook to fetch publication categories
export function usePublicationCategories() {
  return useQuery<ApiResponse<PublicationCategory[]>, Error>({
    queryKey: publicationKeys.categories(),
    queryFn: () => publicationService.getPublicationCategories(),
  })
}

// Hook to search publications
export function useSearchPublications(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Publication>>, Error>({
    queryKey: publicationKeys.search(searchBy || '', params),
    queryFn: () => publicationService.searchPublications(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })
}

// Hook to fetch publication highlights (Key Resources)
export function usePublicationHighlights(type: string = 'HOME_PAGE_KEY_RESOURCES') {
  return useQuery<ApiResponse<Publication[]>, Error>({
    queryKey: publicationKeys.highlights(type),
    queryFn: () => publicationService.getPublicationHighlights(type),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Hook to update publication highlights (Key Resources)
export function useUpdatePublicationHighlights() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<null>, Error, { publicationIds: string[], type?: string }>({
    mutationFn: ({ publicationIds, type = 'HOME_PAGE_KEY_RESOURCES' }) =>
      publicationService.updatePublicationHighlights(publicationIds, type),
    onSuccess: () => {
      // Invalidate the highlights query to refetch
      queryClient.invalidateQueries({ queryKey: publicationKeys.all })
    },
  })
}
